import React, { useState, useMemo } from 'react';
import styles from './Details.module.css';
import dateFunctions from '../../../../utils/dateFunctions.js';
import campaignValues from '../../../../utils/campaignValues.js';
import numberFormatter from '../../../../utils/numberFormatter.js';
import QueryStringService from '../../../../services/queryStringService.js';
import { CAMPAING_PROMISE_TYPES, COUNTRY_CODES, CAMPAIGN_STATUS } from '../../../../constants/enums';
import infoBoxCommon from '../../InfoBox/InfoBoxCommon';
import { useLp5Configuration } from '../../Contexts/Lp5ConfigurationContext';
import { resolvePromiseValues } from '../../../../utils/promiseFieldResolver';

const Details = ({
    selectedOrderlineMetaData,
    orderlinesData,
    orderData,
    campaignPromiseType
}) => {
    const queryStringService = useMemo(() => new QueryStringService(), []);
    const [countryCode] = useState(queryStringService.getCurrentCountry());
    const { promiseConfig } = useLp5Configuration();
    const promisesAwaitingData = ['scv'];
    const postedPanelsDelivered = useMemo(() => {
        if (!orderlinesData) return null;
        const mergedPostingData = [
            ...(orderlinesData.postingData || []),
            ...(orderlinesData.podPostingData || [])
        ];
        return infoBoxCommon.getClassicPanelsDelivered(mergedPostingData);
    }, [orderlinesData]);
    const selectedMetadata = useMemo(() =>
        selectedOrderlineMetaData?.[0] || null
        , [selectedOrderlineMetaData]);

    const idsExportFileExists = orderlinesData?.orderLineDataInformation?.idsExportFileExists ?? false;
    const formatValue = (value, isPercent) => {
        if (isPercent) {
            return `${numberFormatter.numberRoundedWithSpaces(value * 100, 2)}`;
        }
        return numberFormatter.numberWithSpaces(value);
    };

    const showProductName = useMemo(() =>
        selectedMetadata &&
        campaignValues.fetchLocalParam5CampaignPromise(selectedMetadata.localParameter5) ===
            CAMPAING_PROMISE_TYPES.NO_AIRPORT_PLAYS_2_0_1,
        [selectedMetadata]
    );

    const isNonMOMSFollowUp = useMemo(() =>
        campaignValues.isNonMOMSFollowUpCampaign(campaignPromiseType),
        [campaignPromiseType]
    );

    const campaignDetails = useMemo(() => [
        {
            label: 'Advertiser',
            value: orderData.customer,
            show: true
        },
        {
            label: 'Campaign',
            value: selectedMetadata ? campaignValues.caluclateCampaignName(
                selectedMetadata.alternativeName,
                orderData.customer,
                selectedMetadata.periodStart,
                selectedMetadata.periodEnd
            ) : '',
            show: true
        },
        {
            label: 'Period',
            value: selectedMetadata ?
                `Week ${dateFunctions.calculateCampaignWeeks(
                    selectedMetadata.periodStart,
                    selectedMetadata.periodEnd
                )}` : 'Week ',
            show: true
        },
        {
            label: 'Product Type',
            value: selectedMetadata ?
                campaignValues.productTypeDescription(countryCode, selectedMetadata.type) : '',
            show: countryCode !== COUNTRY_CODES.DENMARK
        },
        {
            label: 'Product',
            value: selectedMetadata?.product || '',
            show: true
        },
        {
            label: 'Product Name',
            value: selectedMetadata?.name || '',
            show: showProductName
        },
        {
            label: 'Promise',
            value: selectedMetadata && promiseConfig ? (() => {
                const { target, suffix, isPercent } = resolvePromiseValues(
                    promiseConfig,
                    selectedMetadata,
                    orderlinesData?.orderLineDataInformation,
                    orderlinesData?.orderLineDataInformation?.idsTarget,
                    countryCode
                );
                return `${formatValue(target, isPercent)}${suffix}`;
            })() : '',
            show: !!promiseConfig?.targetField && !isNonMOMSFollowUp
        },
        {
            label: promisesAwaitingData.includes(campaignPromiseType) && !idsExportFileExists ? 'Ongoing delivery' : 'Delivered',
            value: orderlinesData && promiseConfig ? (() => {
                const { delivered, suffix, isPercent } = resolvePromiseValues(
                    promiseConfig,
                    selectedMetadata,
                    orderlinesData.orderLineDataInformation,
                    orderlinesData.orderLineDataInformation.idsTarget,
                    countryCode
                );
                if (promisesAwaitingData.includes(campaignPromiseType) && delivered === 0) {
                    return 'Awaiting Outdoor Impact result';
                }
                return `${formatValue(delivered, isPercent)}${suffix}`;
            })() : '',
            show: !!promiseConfig &&
                selectedMetadata &&
                !isNonMOMSFollowUp &&
                (!promisesAwaitingData.includes(campaignPromiseType) ||
                    campaignValues.campaignStatus(selectedMetadata.periodStart, selectedMetadata.periodEnd) !== CAMPAIGN_STATUS.NOT_STARTED)
        },
        {
            label: 'Posted panels*',
            value: postedPanelsDelivered != null ? numberFormatter.numberWithOneDec(postedPanelsDelivered) : '',
            show: promisesAwaitingData.includes(campaignPromiseType) &&
                selectedMetadata &&
                campaignValues.campaignStatus(selectedMetadata.periodStart, selectedMetadata.periodEnd) !== CAMPAIGN_STATUS.NOT_STARTED
        }
    ], [
        selectedMetadata,
        orderData,
        countryCode,
        showProductName,
        campaignPromiseType,
        orderlinesData,
        promiseConfig,
        postedPanelsDelivered,
        idsExportFileExists,
        isNonMOMSFollowUp
    ]);

    const hasWideLabelRow = promisesAwaitingData.includes(campaignPromiseType) &&
        !!selectedMetadata &&
        campaignValues.campaignStatus(selectedMetadata.periodStart, selectedMetadata.periodEnd) !== CAMPAIGN_STATUS.NOT_STARTED;
    return (
        <div className={styles.container} role="region" aria-label="Campaign Details">
            <table className={styles.table}>
                <colgroup>
                    <col style={{ width: hasWideLabelRow ? '135px' : '120px' }} />
                    <col />
                </colgroup>
                <tbody>
                    {campaignDetails
                        .filter(detail => detail.show)
                        .map(({ label, value }, index) => (
                            <tr key={label} className={index === 0 ? styles.firstRow : ''}>
                                <td className={styles.boldText}>{label}:</td>
                                <td>{value}</td>
                            </tr>
                        ))}
                </tbody>
            </table>
        </div>
    );
};

export default React.memo(Details);